import React, { useState } from 'react';
import styles from './index.module.scss';
import { Loader2, Mail } from 'lucide-react';

const SupportTicket = () => {
  const [formData, setFormData] = useState({
    email: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const submitData = {
        email: formData.email,
        message: formData.message,
      };
      
      // Use environment variable directly
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/support-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setFormData({
        email: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting ticket:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      message: ''
    });
    setErrors({});
    setSubmitStatus(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <div className={styles.header}>
          <h1 className={styles.title}>Contact Support</h1>
          <p className={styles.subtitle}>
            Send us a message and we'll get back to you as soon as possible
          </p>
        </div>

        {submitStatus === 'success' && (
          <div className={styles.successMessage}>
            <div className={styles.successIcon}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className={styles.successTitle}>Message Sent Successfully!</h3>
              <p className={styles.successText}>
                We've received your message. We will get back to you as soon as possible. 
              </p>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className={styles.errorMessage}>
            <div className={styles.errorIcon}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h3 className={styles.errorTitle}>Submission Failed</h3>
              <p className={styles.errorText}>
                There was an error sending your message. Please try again or contact us directly.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Email Field */}
          <div className={styles.fieldGroup}>
            <label htmlFor="email" className={styles.label}>
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
              placeholder="Enter your email address"
            />
            {errors.email && <span className={styles.errorText}>{errors.email}</span>}
          </div>

          {/* Message Field */}
          <div className={styles.fieldGroup}>
            <label htmlFor="message" className={styles.label}>
              Message *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              className={`${styles.textarea} ${errors.message ? styles.inputError : ''}`}
              placeholder="Describe your issue or question..."
              rows={6}
            />
            {errors.message && <span className={styles.errorText}>{errors.message}</span>}
          </div>

          {/* Action Buttons */}
          <div className={styles.buttonGroup}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 style={{width: '16px', height: '16px'}} />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  {/* <Mail style={{width: '16px', height: '16px'}} /> */}
                  <span>Send Message</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Contact Information */}
        <div className={styles.footer}>
          <p>
            Need help? Contact us at{' '}
            <a href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL}`}>
              {import.meta.env.VITE_SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportTicket;
